import create from 'zustand';
import produce from 'immer';

import paywallApi from 'lib/api/paywall';
import {
  ICounterPayload,
  IFeatureData,
  IMultiplePayload,
  IPaywallAction,
  IPaywallPayload,
  TPaywallState,
  ISectionData,
  TSectionType,
} from '../types';

const sectionState: ISectionData = {
  isLoading: false,
  isRenewal: false,
  isPro: false,
  data: {
    features: [],
    last_subscription: null,
    company: null,
  },
};

const initialState: TPaywallState = {
  page: sectionState,
  company: sectionState,
  widget: sectionState,
};

type PaywallStore = TPaywallState & IPaywallAction;

const useStorePaywall = create<PaywallStore>((set) => ({
  ...initialState,
  getStatus: async (payload: IPaywallPayload | IMultiplePayload) => {
    try {
      const { key, feature, company } = payload;
      const isMultiple = Array.isArray(feature) && feature.length > 1;
      const payloadFeature = isMultiple ? feature[0] : feature;

      set(
        produce((state: TPaywallState) => {
          state[key].isLoading = true;
        }),
      );

      const response = isMultiple
        ? await paywallApi.getMultipleStatus(payload as IMultiplePayload)
        : await paywallApi.getPaywallStatus(payload as IPaywallPayload);

      if (!response || !response.data || !response.data.data) {
        throw new Error('Error while fetching paywall status');
      }

      const { data } = response.data;

      const newData = {
        isPro: false,
        isRenewal: data?.last_subscription !== null ?? false,
        isLoading: false,
        data,
      };

      if (company && data.company) {
        newData.isPro = data.company?.is_eligible ?? false;

        set(
          produce((state: TPaywallState) => {
            Object.assign(state[key], newData);
          }),
        );

        return data;
      }

      const selectedData: IFeatureData | undefined = data.features?.find(
        (item: IFeatureData) => item.feature === payloadFeature,
      );

      if (selectedData) {
        newData.isPro = selectedData.is_eligible ?? false;
      }

      set(
        produce((state: TPaywallState) => {
          Object.assign(state[key], newData);
        }),
      );

      return data;
    } catch (err) {
      const defaultError = 'Unknown error while fetching paywall status';

      if (err instanceof Error) {
        console.error(`Error while fetching paywall status: ${err.message}`);
        return err.message;
      }

      console.error(defaultError);
      return defaultError;
    } finally {
      set(
        produce((state: TPaywallState) => {
          state[payload.key].isLoading = false;
        }),
      );
    }
  },
  addCounter: async (payload: ICounterPayload) => {
    try {
      const response = await paywallApi.addPaywallCounter(payload);

      if (!response.data) {
        throw new Error('Error while post paywall counter');
      }

      const { error, error_type: errorType, message } = response.data;

      if (error || errorType) {
        throw new Error(message);
      }

      return;
    } catch (err) {
      if (err instanceof Error) {
        console.error(`Error while post paywall counter: ${err.message}`);
        return;
      }

      console.error('Unknown error while post paywall counter');
    }
  },
  resetProStatus: (key: TSectionType) => {
    set(
      produce((state: TPaywallState) => {
        state[key].isPro = false;
      }),
    );
  },
}));

export default useStorePaywall;
